/**
 * importQueueStore — Fila de processamento para a Janela Única "Adicionar ao Bigwa"
 *
 * Gerencia o ciclo de vida de arquivos desde o drop/seleção até o processamento final.
 * Sobrevive ao unmount da modal (Zustand, não useState).
 *
 * Maquina de estados:
 *   idle → analyzing → disambiguating | processing → complete → idle
 *
 * Separação de responsabilidades:
 *   - importQueueStore: File blobs, roteamento, desambiguação, fila
 *   - importProgressStore: Contadores de progresso (inalterado)
 *   - ImportProgressPanel: Renderiza QueueContent quando este store está ativo
 */

import { create } from 'zustand';
import { nanoid } from 'nanoid';
import { detectDoiLightweight } from '../services/pdfDoiDetector';
import { detectPdfKind } from '../services/pdfKindHeuristic';

// Lazy import para evitar dependência circular
let _processQueue: (() => Promise<void>) | null = null;
async function triggerProcessing() {
  if (!_processQueue) {
    const mod = await import('../services/importQueueProcessor');
    _processQueue = mod.processQueue;
  }
  _processQueue();
}
import {
  FILE_TYPE_MAP,
  getFileExtension,
  getFileTypeConfig,
  isFileSizeValid,
} from '../utils/fileTypeMapping';
import type { FileTypeConfig } from '../utils/fileTypeMapping';
import type { PickedFile } from '../services/googlePicker';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type QueuePhase = 'idle' | 'analyzing' | 'disambiguating' | 'processing' | 'complete';

export type FileRoute = 'biblioteca' | 'repositorio';
export type RouteStatus = 'pending' | 'analyzing' | 'auto-routed' | 'needs-disambiguation' | 'user-resolved';
export type ProcessStatus = 'queued' | 'processing' | 'success' | 'error';

export interface QueuedFile {
  id: string;
  file: File;
  fileName: string;
  extension: string;
  fileTypeConfig: FileTypeConfig | null;

  // Roteamento
  route: FileRoute | null;
  routeStatus: RouteStatus;
  routeReason: string;
  doi: string | null;
  isbn: string | null;

  // Processamento
  processStatus: ProcessStatus;
  error?: string;

  // Tarefa 7.B — confidence gate. Marcado como true quando o extractor
  // retorna metadata.needsReview (confidence < threshold ou sem título).
  // Não bloqueia o pipeline — apenas sinaliza no QueueComplete.
  lowConfidence?: boolean;

  // Tarefa 7.C — heurística de keywords. Quando o detector encontra
  // padrões típicos de dado empírico (voto, prontuário, transcrição),
  // sugere o destino antes do usuário escolher. Nunca decide sozinho.
  suggestedDestination?: FileRoute;
  detectedKind?: import('../services/pdfKindHeuristic').DetectedKind;

  // Drive Picker (se aplicável)
  pickedFile?: PickedFile;
}

export interface ImportQueueState {
  // Estado
  phase: QueuePhase;
  files: QueuedFile[];

  // Ações
  enqueueFiles: (files: File[], pickedFiles?: PickedFile[]) => void;
  resolveDisambiguation: (fileId: string, destination: FileRoute) => void;
  resolveAllDisambiguations: (destination: FileRoute) => void;
  startProcessing: () => void;
  updateFileProcess: (fileId: string, updates: Partial<Pick<QueuedFile, 'processStatus' | 'error'>>) => void;
  completeFile: (fileId: string, success: boolean, error?: string) => void;
  /** Tarefa 7.B — marca o arquivo como "precisa revisão" sem bloquear o pipeline. */
  markFileLowConfidence: (fileId: string) => void;
  completeQueue: () => void;
  cancel: () => void;
  reset: () => void;

  // DOI detection — roda automaticamente após enqueueFiles
  analyzePdfs: () => Promise<void>;
  updateFileRoute: (fileId: string, updates: Partial<Pick<QueuedFile, 'route' | 'routeStatus' | 'routeReason' | 'doi' | 'isbn'>>) => void;
  checkAndAdvancePhase: () => void;

  // Computed helpers (derivados)
  pendingDisambiguations: () => QueuedFile[];
  allResolved: () => boolean;
  isActive: () => boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Allowlist — arquivos aceitos pela Janela Única
// .bib e .ris não estão no FILE_TYPE_MAP (não são "dados de pesquisa")
// mas são aceitos para importação de referências.
// ─────────────────────────────────────────────────────────────────────────────

const BIBLIOGRAPHY_EXTENSIONS = new Set(['bib', 'ris']);

export function isFileAllowed(fileName: string): boolean {
  const ext = getFileExtension(fileName);
  if (!ext) return false;
  return ext in FILE_TYPE_MAP || BIBLIOGRAPHY_EXTENSIONS.has(ext);
}

// ─────────────────────────────────────────────────────────────────────────────
// Roteamento por extensão — instantâneo, sem I/O
// ─────────────────────────────────────────────────────────────────────────────

type ExtensionRoute = 'biblioteca' | 'repositorio' | 'analisar-pdf';

function routeByExtension(fileName: string): ExtensionRoute {
  const ext = getFileExtension(fileName);

  // Biblioteca direta
  if (ext === 'bib' || ext === 'ris') return 'biblioteca';

  // PDF precisa de análise async (DOI check)
  if (ext === 'pdf') return 'analisar-pdf';

  // Tudo no FILE_TYPE_MAP que não é PDF → repositório
  if (FILE_TYPE_MAP[ext]) return 'repositorio';

  // Fallback (não deveria chegar aqui se allowlist funciona)
  return 'repositorio';
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useImportQueueStore = create<ImportQueueState>()((set, get) => ({
  phase: 'idle',
  files: [],

  enqueueFiles: (rawFiles, pickedFiles) => {
    const newFiles: QueuedFile[] = [];

    for (let i = 0; i < rawFiles.length; i++) {
      const file = rawFiles[i];
      const picked = pickedFiles?.[i];

      // Validação de allowlist
      if (!isFileAllowed(file.name)) continue;

      // Validação de tamanho
      if (file.size > 0 && !isFileSizeValid(file.size)) continue;

      const ext = getFileExtension(file.name);
      const extensionRoute = routeByExtension(file.name);

      let route: FileRoute | null = null;
      let routeStatus: RouteStatus = 'pending';
      let routeReason = '';

      if (extensionRoute === 'biblioteca') {
        route = 'biblioteca';
        routeStatus = 'auto-routed';
        routeReason = ext === 'bib' ? 'BibTeX' : 'RIS';
      } else if (extensionRoute === 'repositorio') {
        route = 'repositorio';
        routeStatus = 'auto-routed';
        routeReason = `Tipo: ${getFileTypeConfig(file.name).label}`;
      } else {
        // PDF — precisa de análise async
        routeStatus = 'analyzing';
        routeReason = 'Analisando PDF...';
      }

      newFiles.push({
        id: nanoid(8),
        file,
        fileName: file.name,
        extension: ext,
        fileTypeConfig: ext in FILE_TYPE_MAP ? getFileTypeConfig(file.name) : null,
        route,
        routeStatus,
        routeReason,
        doi: null,
        isbn: null,
        processStatus: 'queued',
        pickedFile: picked,
      });
    }

    if (newFiles.length === 0) return;

    set({ files: newFiles, phase: 'analyzing' });

    // Se nenhum arquivo precisa de análise, avançar imediatamente
    const needsAnalysis = newFiles.some(f => f.routeStatus === 'analyzing');
    if (!needsAnalysis) {
      // Todos auto-roteados — pular direto para processing
      set({ phase: 'processing' });
      triggerProcessing();
    } else {
      // Disparar análise async de PDFs (fire-and-forget — store atualiza via updateFileRoute)
      get().analyzePdfs();
    }
  },

  analyzePdfs: async () => {
    const { files } = get();
    const pdfsToAnalyze = files.filter(f => f.routeStatus === 'analyzing');
    if (pdfsToAnalyze.length === 0) return;

    // Processar em paralelo, max 3 simultâneos
    const MAX_CONCURRENT = 3;
    for (let i = 0; i < pdfsToAnalyze.length; i += MAX_CONCURRENT) {
      const batch = pdfsToAnalyze.slice(i, i + MAX_CONCURRENT);
      await Promise.all(
        batch.map(async (qf) => {
          try {
            const result = await detectDoiLightweight(qf.file);

            // Hotfix #2 (07/abr/2026) — heurística empírica tem PRECEDÊNCIA
            // sobre DOI/ISBN. Razão: textos jurídicos brasileiros (votos, acórdãos)
            // geram FALSO POSITIVO em DOI_PATTERN/ISBN_PATTERN porque contêm
            // referências como "Lei 10.345/2020", "REsp 10.123.456/SP", números
            // de processo, etc. Sem essa precedência, o pipeline rotearia o voto
            // direto para Biblioteca apesar de a heurística ter detectado
            // claramente "voto + ministro + ementa". A heurística é conservadora
            // (minMatches=2 keywords distintas), então o falso positivo dela é
            // muito raro — trade-off aceitável vs deixar votos jurídicos virarem
            // "referências" com metadata inventada.
            const kindResult = detectPdfKind(result.extractedText);
            const empiricalDetected = kindResult.destination === 'repositorio' && kindResult.kind;

            if (empiricalDetected) {
              // Heurística empírica forte → desambiguação manual com sugestão
              // (mesmo que DOI/ISBN tenha sido detectado, provavelmente é falso positivo)
              get().updateFileRoute(qf.id, {
                routeStatus: 'needs-disambiguation',
                routeReason: 'Conteúdo parece dado empírico — confirme o destino',
              });
              set(state => ({
                files: state.files.map(f =>
                  f.id === qf.id
                    ? {
                        ...f,
                        suggestedDestination: 'repositorio' as FileRoute,
                        detectedKind: kindResult.kind,
                      }
                    : f
                ),
              }));
            } else if (result.hasDoi || result.hasIsbn) {
              // DOI ou ISBN encontrado E sem heurística empírica → biblioteca
              get().updateFileRoute(qf.id, {
                route: 'biblioteca',
                routeStatus: 'auto-routed',
                routeReason: result.hasDoi
                  ? `DOI: ${result.doi}`
                  : `ISBN: ${result.isbn}`,
                doi: result.doi,
                isbn: result.isbn,
              });
            } else {
              // Sem DOI/ISBN E sem detecção de heurística → desambiguação manual
              get().updateFileRoute(qf.id, {
                routeStatus: 'needs-disambiguation',
                routeReason: 'PDF sem DOI — escolha o destino',
              });
            }
          } catch {
            // Erro na análise → desambiguação (fallback seguro)
            get().updateFileRoute(qf.id, {
              routeStatus: 'needs-disambiguation',
              routeReason: 'Não foi possível analisar — escolha o destino',
            });
          }
        })
      );
    }

    // Após todas as análises, avançar fase
    get().checkAndAdvancePhase();
  },

  updateFileRoute: (fileId, updates) => {
    set(state => ({
      files: state.files.map(f =>
        f.id === fileId ? { ...f, ...updates } : f
      ),
    }));
  },

  checkAndAdvancePhase: () => {
    const { files, phase } = get();
    if (phase !== 'analyzing') return;

    // Algum arquivo ainda está sendo analisado?
    const stillAnalyzing = files.some(f => f.routeStatus === 'analyzing');
    if (stillAnalyzing) return;

    // Todos terminaram a análise — verificar se há desambiguação
    const needsDisambiguation = files.some(f => f.routeStatus === 'needs-disambiguation');
    if (needsDisambiguation) {
      set({ phase: 'disambiguating' });
    } else {
      set({ phase: 'processing' });
      triggerProcessing();
    }
  },

  resolveDisambiguation: (fileId, destination) => {
    set(state => ({
      files: state.files.map(f =>
        f.id === fileId
          ? { ...f, route: destination, routeStatus: 'user-resolved' as RouteStatus, routeReason: `Escolha do pesquisador: ${destination}` }
          : f
      ),
    }));

    // Verificar se todos foram resolvidos
    const { files, phase } = get();
    if (phase !== 'disambiguating') return;

    const stillPending = files.some(f => f.routeStatus === 'needs-disambiguation');
    if (!stillPending) {
      set({ phase: 'processing' });
      triggerProcessing();
    }
  },

  resolveAllDisambiguations: (destination) => {
    set(state => ({
      files: state.files.map(f =>
        f.routeStatus === 'needs-disambiguation'
          ? { ...f, route: destination, routeStatus: 'user-resolved' as RouteStatus, routeReason: `Escolha do pesquisador: ${destination}` }
          : f
      ),
      phase: 'processing',
    }));
    triggerProcessing();
  },

  startProcessing: () => {
    set({ phase: 'processing' });
  },

  updateFileProcess: (fileId, updates) => {
    set(state => ({
      files: state.files.map(f =>
        f.id === fileId ? { ...f, ...updates } : f
      ),
    }));
  },

  completeFile: (fileId, success, error) => {
    set(state => ({
      files: state.files.map(f =>
        f.id === fileId
          ? { ...f, processStatus: success ? 'success' : 'error', error }
          : f
      ),
    }));
  },

  markFileLowConfidence: (fileId) => {
    set(state => ({
      files: state.files.map(f =>
        f.id === fileId ? { ...f, lowConfidence: true } : f
      ),
    }));
  },

  completeQueue: () => {
    set({ phase: 'complete' });
  },

  cancel: () => {
    set({ phase: 'idle', files: [] });
  },

  reset: () => {
    set({ phase: 'idle', files: [] });
  },

  // Computed helpers
  pendingDisambiguations: () => {
    return get().files.filter(f => f.routeStatus === 'needs-disambiguation');
  },

  allResolved: () => {
    return get().files.every(
      f => f.routeStatus === 'auto-routed' || f.routeStatus === 'user-resolved'
    );
  },

  isActive: () => {
    return get().phase !== 'idle';
  },
}));
